// src/components/VideoAdModal.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, LinearProgress, IconButton, GlobalStyles, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { TOP_APP_BAR_HEIGHT } from "../constants/constants";
import {
  evaluatePublicCapsuleAdEligibility,
  recordPublicCapsuleAdShown,
} from "../utils/adPolicy";

const VAST_TAG_URL =
  "https://pubads.g.doubleclick.net/gampad/adx?iu=/5021179/inssa&sz=2x2&c=635574567&cust_params=campaign%3Dinssa-concessionaire-home&m=text/xml";

// Storage helper: use localStorage if possible, mirror to cookie as best effort.
const Storage = {
  get(key) {
    try {
      const v = window.localStorage.getItem(key);
      if (v !== null) return v;
    } catch {}
    try {
      const m = document.cookie.match(
        new RegExp("(?:^|; )" + encodeURIComponent(key) + "=([^;]*)")
      );
      return m ? decodeURIComponent(m[1]) : null;
    } catch {}
    return null;
  },
  set(key, value, maxAgeSeconds = 60 * 60 * 24 * 365) {
    let ok = false;
    try {
      window.localStorage.setItem(key, value);
      ok = true;
    } catch {}
    try {
      document.cookie =
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}; ` +
        `max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
    } catch {}
    return ok;
  }
};

// Layer constants
const Z = {
  AD_UI: 1000,
  CONTROLS: 10000,
};

export default function VideoAdModal({
  disabled = false,
  forceShow = false,
  instanceKey = "",
  anchoredAboveFooter = false,
  anchorBottom = 0,
  capsuleType = "",
  capsuleId = "",
  policy,
  onClose,
}) {
  const theme = useTheme();
  const videoRef = useRef(null);
  const adContainerRef = useRef(null);

  // IMA refs
  const adsManagerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adDisplayContainerRef = useRef(null);
  const adActiveRef = useRef(false);

  const [canShowAd, setCanShowAd] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  // UI state
  const [modalVisible, setModalVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showClose, setShowClose] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const isMutedRef = useRef(true); // source of truth inside IMA event handlers

  // internals
  const progressTimerRef = useRef(null);
  const gestureTimerRef = useRef(null);
  const [needGesture, setNeedGesture] = useState(false);

  // Callers pass an inline arrow for onClose, so its identity changes on every
  // parent render. Read it through a ref to keep handleClose stable - otherwise
  // it invalidates the IMA effect below and restarts the ad on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const sessionFallbackRef = useRef({});
  const readSession = useCallback((key) => {
    try {
      if (typeof window !== "undefined") {
        const value = window.sessionStorage.getItem(key);
        if (value !== null) return value;
      }
    } catch {}
    return key in sessionFallbackRef.current
      ? String(sessionFallbackRef.current[key])
      : null;
  }, []);

  const writeSession = useCallback((key, value) => {
    let ok = false;
    try {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(key, value);
        ok = true;
      }
    } catch {}
    sessionFallbackRef.current[key] = String(value);
    return ok;
  }, []);

  const handleClose = useCallback(() => {
    try { adsManagerRef.current?.destroy(); } catch {}
    adsManagerRef.current = null;
    adActiveRef.current = false;

    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }

    setModalVisible(false);
    setProgress(0);
    setShowClose(false);
    setIsMuted(true);
    isMutedRef.current = true;
    setSdkReady(false);
    setNeedGesture(false);
    if (typeof onCloseRef.current === "function") {
      onCloseRef.current();
    }
  }, []);

  // policy + external disable gate
  useEffect(() => {
    if (disabled) {
      handleClose();
      setCanShowAd(false);
      return;
    }
    if (forceShow) {
      setCanShowAd(true);
      return;
    }
    const eligibility = evaluatePublicCapsuleAdEligibility({
      capsuleType,
      capsuleId,
      instanceKey,
      disabled,
      policy,
      readLocal: Storage.get,
      writeLocal: Storage.set,
      readSession,
      writeSession,
    });
    setCanShowAd(Boolean(eligibility.canShow));
  }, [capsuleId, capsuleType, disabled, forceShow, handleClose, instanceKey, policy, readSession, writeSession]);

  // Load IMA SDK when planning to show
  useEffect(() => {
    if (!canShowAd || disabled) return;
    if (window.google?.ima) {
      setSdkReady(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";
    script.async = true;
    script.onload = () => setSdkReady(true);
    document.head.appendChild(script);
  }, [canShowAd, disabled]);

  // IMA ad flow
  useEffect(() => {
    if (!sdkReady || !canShowAd || disabled) return;

    const videoEl = videoRef.current;
    const adUI = adContainerRef.current;
    if (!videoEl || !adUI) return;

    // Ensure inline playback and no native controls/interaction
    videoEl.setAttribute("playsinline", "");
    videoEl.setAttribute("webkit-playsinline", "true");
    videoEl.controls = false;
    videoEl.muted = true;
    videoEl.volume = 0;
    videoEl.style.pointerEvents = "none";

    // IMA overlay below our controls
    adUI.style.zIndex = String(Z.AD_UI);

    const adDisplayContainer = new window.google.ima.AdDisplayContainer(adUI, videoEl);
    adDisplayContainerRef.current = adDisplayContainer;

    const adsLoader = new window.google.ima.AdsLoader(adDisplayContainer);
    adsLoaderRef.current = adsLoader;

    const clearGestureTimer = () => {
      if (gestureTimerRef.current) {
        clearTimeout(gestureTimerRef.current);
        gestureTimerRef.current = null;
      }
    };

    const onAdsManagerLoaded = (e) => {
      const adsManager = e.getAdsManager(videoEl);
      adsManagerRef.current = adsManager;

      adsManager.addEventListener(
        window.google.ima.AdErrorEvent.Type.AD_ERROR,
        (err) => {
          console.info("[IMA] AD_ERROR:", err?.getError?.()?.toString?.() || err);
          try { adsManager.destroy(); } catch {}
          adsManagerRef.current = null;
          adActiveRef.current = false;
          clearGestureTimer();
          handleClose();
        }
      );

      adsManager.addEventListener(
        window.google.ima.AdEvent.Type.PAUSED,
        () => {
          if (adActiveRef.current) {
            try { adsManager.resume(); } catch {}
          }
        }
      );

      adsManager.addEventListener(
        window.google.ima.AdEvent.Type.STARTED,
        () => {
          if (disabled) {
            try { adsManager.destroy(); } catch {}
            adsManagerRef.current = null;
            adActiveRef.current = false;
            clearGestureTimer();
            handleClose();
            return;
          }

          adActiveRef.current = true;
          setModalVisible(true);
          setNeedGesture(false);
          clearGestureTimer();

          // mark policy counters and start progress
          if (!forceShow) {
            recordPublicCapsuleAdShown({
              capsuleId,
              policy,
              readLocal: Storage.get,
              writeLocal: Storage.set,
              readSession,
              writeSession,
            });
          }
          startProgressTimer();

          // Use the ref so this effect does not depend on isMuted
          applyVolume(isMutedRef.current);
        }
      );

      const onAdDone = () => {
        adActiveRef.current = false;
        try { adsManager.destroy(); } catch {}
        adsManagerRef.current = null;
        handleClose();
      };

      adsManager.addEventListener(
        window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
        onAdDone
      );
      adsManager.addEventListener(
        window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
        onAdDone
      );

      // Init & start ad
      adsManager.init(
        videoEl.clientWidth || 640,
        videoEl.clientHeight || 360,
        window.google.ima.ViewMode.NORMAL
      );
      try {
        adsManager.start();
      } catch (err) {
        console.info("[IMA] start() threw:", err);
        try { adsManager.destroy(); } catch {}
        adsManagerRef.current = null;
        adActiveRef.current = false;
        clearGestureTimer();
        setNeedGesture(true);
      }
    };

    const onAdsLoaderError = (err) => {
      console.info("[IMA] AdsLoader AD_ERROR:", err?.getError?.()?.toString?.() || err);
      adsManagerRef.current = null;
      adActiveRef.current = false;
      clearGestureTimer();
      setNeedGesture(true);
      handleClose();
    };

    adsLoader.addEventListener(
      window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      onAdsManagerLoaded
    );
    adsLoader.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      onAdsLoaderError
    );

    // Kick off the ad request on first play
    const onFirstPlay = () => {
      try { adDisplayContainer.initialize(); } catch {}
      const adsRequest = new window.google.ima.AdsRequest();
      adsRequest.adTagUrl = VAST_TAG_URL;
      adsRequest.linearAdSlotWidth = videoEl.clientWidth || 640;
      adsRequest.linearAdSlotHeight = videoEl.clientHeight || 360;
      adsRequest.nonLinearAdSlotWidth = videoEl.clientWidth || 640;
      adsRequest.nonLinearAdSlotHeight = (videoEl.clientHeight || 360) / 3;
      adsLoader.requestAds(adsRequest);

      // If STARTED does not arrive quickly, show gesture fallback
      if (!gestureTimerRef.current) {
        gestureTimerRef.current = setTimeout(() => {
          if (!adActiveRef.current) setNeedGesture(true);
        }, 2000);
      }
    };

    videoEl.addEventListener("play", onFirstPlay, { once: true });
    videoEl.play().catch(() => {});

    return () => {
      videoEl.removeEventListener("play", onFirstPlay);
      clearGestureTimer();
      // Tear the ad down with the effect that created it. Without this, a
      // re-run leaves the previous ad playing and only the newest one is
      // tracked in adsManagerRef, so closing cannot stop the earlier ones.
      try { adsManagerRef.current?.destroy(); } catch {}
      adsManagerRef.current = null;
      adActiveRef.current = false;
      try { adsLoader.destroy?.(); } catch {}
      adsLoaderRef.current = null;
    };
  }, [sdkReady, canShowAd, disabled, capsuleId, forceShow, handleClose, instanceKey, policy, readSession, writeSession]); // no isMuted here

  // Volume application used by STARTED handler and toggle
  const applyVolume = (muted) => {
    const v = videoRef.current;
    const m = adsManagerRef.current;
    if (v) {
      v.muted = muted;
      v.volume = muted ? 0 : 1;
    }
    if (m) {
      try { m.setVolume(muted ? 0 : 1); } catch {}
    }
  };

  const startProgressTimer = () => {
    setProgress(0);
    setShowClose(false);

    const start = Date.now();
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);

    const id = setInterval(() => {
      const pct = Math.min(((Date.now() - start) / 5000) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(id);
        progressTimerRef.current = null;
        setShowClose(true);
      }
    }, 100);

    progressTimerRef.current = id;
  };

  const swallow = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Press handlers must NOT preventDefault. Canceling pointerdown suppresses the
  // compatibility mouse events on touch, so the synthesized click never arrives
  // and the control does nothing on a phone. Canceling touchstart is a no-op
  // anyway - React listens passively - and only logs a console warning.
  // https://w3c.github.io/pointerevents/#compatibility-mapping-with-mouse-events
  const stopOnly = (e) => {
    e.stopPropagation();
  };

  const swallowThenClose = (e) => {
    swallow(e);
    handleClose();
  };

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    isMutedRef.current = next;    // keep ref in sync for IMA handlers
    applyVolume(next);
  };

  // Gesture fallback: run IMA init and request ads strictly inside a user click.
  const beginWithGesture = (e) => {
    swallow(e);
    const videoEl = videoRef.current;
    const adsLoader = adsLoaderRef.current;
    const adDisplayContainer = adDisplayContainerRef.current;
    if (!videoEl || !adsLoader || !window.google?.ima) return;

    try { adDisplayContainer?.initialize(); } catch {}
    try {
      videoEl.muted = true;
      videoEl.play().catch(() => {});
    } catch {}

    const req = new window.google.ima.AdsRequest();
    req.adTagUrl = VAST_TAG_URL;
    req.linearAdSlotWidth = videoEl.clientWidth || 640;
    req.linearAdSlotHeight = videoEl.clientHeight || 360;
    req.nonLinearAdSlotWidth = videoEl.clientWidth || 640;
    req.nonLinearAdSlotHeight = (videoEl.clientHeight || 360) / 3;
    adsLoader.requestAds(req);

    setNeedGesture(false);
  };

  if (!canShowAd || disabled) return null;

  const resolvedAnchorBottom =
    typeof anchorBottom === "number" ? `${anchorBottom}px` : String(anchorBottom || "0px");

  return (
    <Box
      sx={{
        position: "fixed",
        top: anchoredAboveFooter ? "auto" : `${TOP_APP_BAR_HEIGHT}px`,
        left: 0,
        right: 0,
        bottom: anchoredAboveFooter ? resolvedAnchorBottom : 0,
        width: "100%",
        height: anchoredAboveFooter ? "auto" : `calc(100vh - ${TOP_APP_BAR_HEIGHT}px)`,
        backgroundColor:
          anchoredAboveFooter
            ? "transparent"
            : (
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.background.default, 0.68)
                  : alpha(theme.palette.background.default, 0.42)
              ),
        backdropFilter: anchoredAboveFooter ? "none" : "blur(8px)",
        WebkitBackdropFilter: anchoredAboveFooter ? "none" : "blur(8px)",
        zIndex: 1000000,
        opacity: modalVisible ? 1 : 0,
        visibility: modalVisible ? "visible" : "hidden",
        transition: "opacity 0.2s ease",
        pointerEvents: modalVisible ? "auto" : "none",
        p: anchoredAboveFooter ? 0 : { xs: 0, md: 2 },
        boxSizing: "border-box",
      }}
    >
      <GlobalStyles
        styles={{
          ".inssa-ima-container": {
            zIndex: `${Z.AD_UI} !important`,
          },
        }}
      />

      <Box
        sx={{
          width: anchoredAboveFooter
            ? { xs: "100%", md: "min(640px, calc(100vw - 48px))" }
            : { xs: "100%", md: "min(960px, calc(100vw - 48px))" },
          mx: "auto",
          aspectRatio: "16/9",
          position: "relative",
          overflow: "hidden",
          borderRadius: anchoredAboveFooter ? 0 : { xs: 0, md: 1 },
          boxShadow: anchoredAboveFooter
            ? "none"
            : { xs: "none", md: `0 10px 30px ${alpha(theme.palette.common.black, 0.22)}` },
        }}
      >
        <video
          ref={videoRef}
          playsInline
          webkit-playsinline="true"
          controls={false}
          style={{ width: "100%", height: "100%", pointerEvents: "none" }}
        />

        {/* IMA ad UI overlay */}
        <Box
          ref={adContainerRef}
          className="inssa-ima-container"
          sx={{ position: "absolute", inset: 0, pointerEvents: "auto" }}
        />

        {/* CONTROLS OVERLAY */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: Z.CONTROLS,
            pointerEvents: "none",
          }}
        >
          {/* Mute */}
          <IconButton
            onClick={(e) => { swallow(e); toggleMute(); }}
            onPointerDown={stopOnly}
            onTouchStart={stopOnly}
            onMouseDown={stopOnly}
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              pointerEvents: "auto",
              backgroundColor: alpha(theme.palette.common.black, 0.5),
              color: theme.colors.white,
              p: "4px",
              "&:hover": { backgroundColor: alpha(theme.palette.common.black, 0.7) },
            }}
            aria-label={isMuted ? "Unmute ad" : "Mute ad"}
          >
            {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
          </IconButton>

          {/* Progress */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "6px",
              borderRadius: 0,
              pointerEvents: "none",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.colors.blue, 0.2)
                  : undefined,
              "& .MuiLinearProgress-bar": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.colors.blue
                    : undefined,
              },
            }}
          />

          {/* Close */}
          {showClose && (
            <IconButton
              onClick={swallowThenClose}
              onPointerDown={stopOnly}
              onTouchStart={stopOnly}
              onMouseDown={stopOnly}
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                pointerEvents: "auto",
                backgroundColor: alpha(theme.palette.common.black, 0.6),
                color: theme.colors.white,
                "&:hover": { backgroundColor: alpha(theme.palette.common.black, 0.8) },
              }}
              aria-label="Close ad"
            >
              <CloseIcon />
            </IconButton>
          )}

          {/* Gesture fallback CTA */}
          {needGesture && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",
                background: `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0.35)}, ${alpha(theme.palette.common.black, 0.35)})`,
              }}
            >
              <Button variant="contained" onClick={beginWithGesture} sx={{ fontWeight: 600 }}>
                Continue
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
